import { ArrowUpRight } from "lucide-react";
import type { ReactNode } from "react";
import type {
  ActiveNode,
  Agent,
  App,
  AppCategory,
  AppStatus,
  Component
} from "../types/platform";
import { agents } from "../data/agents";
import { apps } from "../data/apps";
import { components } from "../data/components";
import { APP_STATUS } from "../data/status";

const ORANGE = "#F28132";

export function InfoPanel({ active }: { active: ActiveNode }) {
  if (!active) return <DefaultPanel />;
  if (active.kind === "agent") return <AgentPanel agent={active.data} />;
  if (active.kind === "component")
    return <ComponentPanel component={active.data} />;
  return <AppPanel app={active.data} />;
}

function consumesFor(appName: string): readonly string[] {
  return components
    .filter((c) => c.consumedBy.includes(appName))
    .map((c) => c.name);
}

function DefaultPanel() {
  const customerCount = apps.filter((a) => a.category === "customer").length;
  const staffCount = apps.filter((a) => a.category === "internal").length;
  const workerCount = apps.filter((a) => a.category === "b2proc").length;

  return (
    <div className="flex h-full flex-col justify-between">
      <div>
        <div
          className="mb-3 text-[10px] uppercase tracking-[0.2em]"
          style={{ color: ORANGE }}
        >
          The Platform
        </div>
        <h2 className="mb-4 text-3xl font-semibold leading-tight text-white">
          Agents orchestrating applications.
        </h2>
        <p className="mb-8 text-sm leading-relaxed text-white/60">
          Eight agents reason across the platform. Eight applications carry
          the work to customers, staff, and workers. Four shared services
          power them all. The center is OpenSesame.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-5 border-t border-white/15 pt-6">
        <Stat label="Agents" value={agents.length} />
        <Stat label="Shared services" value={components.length} />
        <Stat label="Customer apps" value={customerCount} />
        <Stat label="Staff apps" value={staffCount} />
        <Stat label="Worker app" value={workerCount} />
        <div aria-hidden="true" />
      </div>

      <div className="mt-6 text-[11px] text-white/30">
        Hover any node to explore. Click an app to launch it.
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="mb-1 text-[10px] uppercase tracking-widest text-white/40">
        {label}
      </div>
      <div className="text-2xl font-light text-white">{value}</div>
    </div>
  );
}

function AgentPanel({ agent }: { agent: Agent }) {
  const Icon = agent.icon;
  const primaryApps = apps.filter((a) => a.agentPartner === agent.name);
  const primaryComponents = components.filter(
    (c) => c.agentPartner === agent.name
  );
  const secondaryApps = apps.filter((a) => a.secondaryAgent === agent.name);

  return (
    <div className="flex h-full flex-col">
      <div className="mb-6 flex items-center gap-3">
        <div
          className="flex items-center justify-center rounded-full"
          style={{ width: 44, height: 44, backgroundColor: ORANGE }}
        >
          <Icon size={20} color="#fff" />
        </div>
        <div>
          <div
            className="text-[10px] uppercase tracking-[0.2em]"
            style={{ color: ORANGE }}
          >
            Agent
          </div>
          <h2 className="text-2xl font-semibold leading-tight text-white">
            {agent.name}
          </h2>
          <div className="mt-1 text-sm text-white/55">{agent.subtitle}</div>
        </div>
      </div>

      <PanelColumns>
        <div>
          <Section title="Why it exists" body={agent.why} tone="bright" />
          <Section title="What it does" body={agent.description} tone="bright" />
          <Section title="How it does it" body={agent.how} tone="muted" />
        </div>
        <div>
          <ChipRow
            label="Primary applications"
            items={[
              ...primaryApps.map((a) => a.name),
              ...primaryComponents.map((c) => c.name)
            ]}
            variant="filled"
          />
          <ChipRow
            label="Also touches"
            items={secondaryApps.map((a) => a.name)}
            variant="outlined"
          />
        </div>
      </PanelColumns>
    </div>
  );
}

/** Stacked below xl; two columns on the fixed xl canvas so long panels fit. */
function PanelColumns({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-1 flex-col gap-6 xl:grid xl:min-h-0 xl:grid-cols-2 xl:gap-7">
      {children}
    </div>
  );
}

function Section({
  title,
  body,
  tone
}: {
  title: string;
  body: string;
  tone: "bright" | "muted";
}) {
  return (
    <div className="mb-6 last:mb-0">
      <div className="mb-2 text-[10px] uppercase tracking-widest text-white/40">
        {title}
      </div>
      <p
        className={`text-sm leading-relaxed ${
          tone === "bright" ? "text-white/80" : "text-white/70"
        }`}
      >
        {body}
      </p>
    </div>
  );
}

const APP_EYEBROW: Record<AppCategory, string> = {
  customer: "Customer app",
  internal: "Staff app",
  b2proc: "Worker app, B2proC"
};

function AppHeader({ app }: { app: App }) {
  return (
    <div className="mb-5">
      <div
        className="mb-1 text-[10px] uppercase tracking-[0.2em]"
        style={{ color: ORANGE }}
      >
        {APP_EYEBROW[app.category]}
      </div>
      <h2 className="text-3xl font-semibold leading-tight text-white">
        {app.name}
      </h2>
      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-sm text-white/55">{app.tagline}</span>
        <StatusBadge status={app.status} />
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: AppStatus }) {
  const { label, line } = APP_STATUS[status];
  return (
    <span
      className="rounded-full px-2 py-0.5 text-[9px] uppercase tracking-widest text-white/70"
      style={{ border: `1px ${line} rgba(255,255,255,0.45)` }}
    >
      {label}
    </span>
  );
}

function AppRelations({ app }: { app: App }) {
  const consumes = consumesFor(app.name);
  return (
    <div className="flex flex-col gap-3 xl:mb-5">
      <div className="flex flex-wrap gap-2">
        {app.agentPartner && (
          <AgentChip name={app.agentPartner} variant="filled" />
        )}
        {app.secondaryAgent && (
          <AgentChip name={app.secondaryAgent} variant="outlined" />
        )}
      </div>
      {consumes.length > 0 && (
        <div>
          <div className="mb-2 text-[10px] uppercase tracking-widest text-white/40">
            Consumes
          </div>
          <div className="flex flex-wrap gap-2">
            {consumes.map((name) => (
              <ServiceChip key={name} name={name} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AppPanel({ app }: { app: App }) {
  return (
    <div className="flex h-full flex-col">
      <AppHeader app={app} />

      <PanelColumns>
        <div>
          <Section title="Why it exists" body={app.why} tone="bright" />
          <Section title="What it does" body={app.description} tone="bright" />
          <Section title="How it does it" body={app.how} tone="muted" />
        </div>
        <div className="flex flex-col">
          <div className="mb-6 xl:mb-5">
            <div className="mb-3 text-[10px] uppercase tracking-widest text-white/40">
              Roadmap
            </div>
            <RoadmapList label="Now" items={app.now} variant="filled" />
            <RoadmapList label="Next" items={app.next} variant="outlined" />
          </div>

          <AppRelations app={app} />

          <a
            href={app.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center justify-between rounded-lg px-4 py-3 font-medium text-white transition-all xl:mt-auto"
            style={{ backgroundColor: ORANGE }}
          >
            <span className="text-sm tracking-wide">Launch {app.name}</span>
            <ArrowUpRight size={18} />
          </a>
        </div>
      </PanelColumns>
    </div>
  );
}

function ComponentPanel({ component }: { component: Component }) {
  return (
    <div className="flex h-full flex-col">
      <div className="mb-6">
        <div
          className="mb-1 text-[10px] uppercase tracking-[0.2em]"
          style={{ color: ORANGE }}
        >
          Shared service
        </div>
        <h2 className="text-3xl font-semibold leading-tight text-white">
          {component.name}
        </h2>
        <div className="mt-1 text-sm text-white/55">{component.tagline}</div>
      </div>

      <PanelColumns>
        <div>
          <Section title="Why it exists" body={component.why} tone="bright" />
          <Section title="What it does" body={component.description} tone="bright" />
          <Section title="How it does it" body={component.how} tone="muted" />
        </div>
        <div className="flex flex-col">
          {component.agentPartner && (
            <div className="mb-5">
              <AgentChip name={component.agentPartner} variant="filled" />
            </div>
          )}

          <ChipRow
            label="Consumed by"
            items={component.consumedBy}
            variant="filled"
          />

          <div className="mb-5 last:mb-auto">
            <div className="mb-2 text-[10px] uppercase tracking-widest text-white/40">
              Access via
            </div>
            <div className="flex flex-wrap gap-2">
              {component.accessVia.map((channel) => (
                <span
                  key={channel}
                  className="rounded-md px-2 py-1 text-[11px]"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    color: "rgba(255,255,255,0.75)"
                  }}
                >
                  {channel}
                </span>
              ))}
            </div>
          </div>

          {component.surfaceUrl && (
            <a
              href={component.surfaceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1 self-start text-[11px] text-white/55 underline-offset-4 transition-colors hover:text-white/85 hover:underline"
            >
              Surfaces in Marketplace
              <ArrowUpRight size={12} />
            </a>
          )}
        </div>
      </PanelColumns>
    </div>
  );
}

function RoadmapList({
  label,
  items,
  variant
}: {
  label: string;
  items: readonly string[];
  variant: "filled" | "outlined";
}) {
  if (items.length === 0) return null;
  return (
    <div className="mb-5 last:mb-auto xl:mb-4">
      <div className="mb-3 text-[10px] uppercase tracking-widest text-white/40">
        {label}
      </div>
      <ul className="space-y-2 xl:space-y-1.5">
        {items.map((item) => (
          <li
            key={item}
            className={`flex gap-3 text-sm ${
              variant === "filled" ? "text-white/80" : "text-white/60"
            }`}
          >
            <span
              className="mt-1.5 h-[5px] w-[5px] flex-shrink-0 rounded-full"
              style={
                variant === "filled"
                  ? { backgroundColor: ORANGE }
                  : { border: `1px solid ${ORANGE}` }
              }
            />
            <span className="leading-relaxed">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ChipRow({
  label,
  items,
  variant
}: {
  label: string;
  items: readonly string[];
  variant: "filled" | "outlined";
}) {
  if (items.length === 0) return null;
  return (
    <div className="mb-5 last:mb-auto">
      <div className="mb-2 text-[10px] uppercase tracking-widest text-white/40">
        {label}
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((item) => (
          <span
            key={item}
            className="rounded-md px-2.5 py-1 text-[11px]"
            style={
              variant === "filled"
                ? {
                    backgroundColor: "rgba(242,129,50,0.12)",
                    border: `1px solid rgba(242,129,50,0.5)`,
                    color: "#F28132"
                  }
                : {
                    backgroundColor: "transparent",
                    border: `1px solid rgba(242,129,50,0.4)`,
                    color: "rgba(242,129,50,0.75)"
                  }
            }
          >
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

function AgentChip({
  name,
  variant
}: {
  name: string;
  variant: "filled" | "outlined";
}) {
  const label = variant === "filled" ? "Primary" : "Secondary";
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px]"
      style={
        variant === "filled"
          ? {
              backgroundColor: "rgba(242,129,50,0.12)",
              border: `1px solid rgba(242,129,50,0.5)`,
              color: "#F28132"
            }
          : {
              backgroundColor: "transparent",
              border: `1px solid rgba(242,129,50,0.4)`,
              color: "rgba(242,129,50,0.75)"
            }
      }
    >
      <span className="text-[9px] uppercase tracking-widest opacity-70">
        {label}
      </span>
      <span>{name}</span>
    </span>
  );
}

function ServiceChip({ name }: { name: string }) {
  return (
    <span
      className="inline-flex items-center rounded-md px-2.5 py-1 text-[11px]"
      style={{
        backgroundColor: "rgba(255,255,255,0.04)",
        border: "1px dashed rgba(255,255,255,0.40)",
        color: "rgba(255,255,255,0.80)"
      }}
    >
      {name}
    </span>
  );
}
